import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { useRouteQuery } from 'src/common/helpers';

import { useDeleteProcessTasksTemplate } from 'src/hooks/process-task-templates/delete-tasks-template.hook';
import { useGetProcessTasksTemplatesForTable } from 'src/hooks/process-task-templates/get-process-tasks-templates.hook';

import PermissionCheck from '../../../../components/permission-check';
import { ProcessTemplatesTable } from './components/process-templates-table';
import { ChangeProcessTemplate } from './components/change-process-template';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';

import { DEFAULT_PROCESS_TEMPLATES, PROCESS_TEMPLATES_MODALS } from './process-templates.constants';

import { ProcessTemplateWrapper } from './process-templates.styles';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';


const ProcessTemplates = () => {
  const navigate = useNavigate();
  const query = useRouteQuery();
  const { t } = useTranslation('settings');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [activeTemplate, setActiveTemplate] = useState(DEFAULT_PROCESS_TEMPLATES);

  const { collection, paginationConfig, loading } = useGetProcessTasksTemplatesForTable();

  const id: string = query.get('id') || '';

  const handleCreateNewTemplate = () => {
    navigate(`${ROUTES.settings}?type=${SETTINGS_PAGES.processTemplates}&id=new`);
  };

  const closeModal = () => {
    setActiveModal(null);
    setActiveTemplate(DEFAULT_PROCESS_TEMPLATES);
  };

  const deleteProcessTasksTemplate = useDeleteProcessTasksTemplate({
    onSuccess: closeModal,
  });

  const deleteTemplate = () => {
    deleteProcessTasksTemplate({
      variables: {
        processTaskTemplateId: activeTemplate?._id,
      },
    });
    setActiveTemplate(DEFAULT_PROCESS_TEMPLATES);
    closeModal();
  };

  const onSubmitDelete = () => {
    deleteTemplate();
  };

  return (
    <Fragment>
      {id ? (
        <ChangeProcessTemplate />
      ) : (
        <ProcessTemplateWrapper>
          <DeleteModal
            translate={t}
            isOpened={activeModal === PROCESS_TEMPLATES_MODALS.delete}
            title={t('processTemplates.deleteModal.title')}
            subTitle={t('processTemplates.deleteModal.subtitle', {
              templateName: activeTemplate?.name,
            })}
            onClose={closeModal}
            onSubmit={onSubmitDelete}
          />
          <div>
            <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
              {t('processTemplates.labels.processTemplates')}
            </Typography>
            <PermissionCheck
              permission={PLATFORM_PERMISSIONS.processBuilder}
              action={PERMISSION_ACTIONS.update}
            >
            <ButtonsWrapper>
              <Button
                type="primary"
                ghost
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={handleCreateNewTemplate}
              >
                {t('processTemplates.labels.addNewProcessTemplate')}
              </Button>
            </ButtonsWrapper>
            </PermissionCheck>
          </div>
          <ProcessTemplatesTable
            dataSource={collection}
            paginationConfig={paginationConfig}
            handleModals={(modal, record) => {
              setActiveModal(modal);
              setActiveTemplate(record);
            }}
            loading={loading}
          />
        </ProcessTemplateWrapper>
      )}
    </Fragment>
  );
};

export default ProcessTemplates;
